import React, { useMemo, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"; // Dodano animacje
import { RenderMarkdownWithLatex } from "../../utils/utils";
import PageTestPage from "../molecules/PageTestPage";
import { readStoredStatus } from "../molecules/Page";
import { useWindowSize } from "../../hooks/useWindowSize";

// Nowe komponenty
import HintAndFormulas from "../molecules/HintAndFormulas";
import SolutionStepper from "../molecules/SolutionStepper";

// --- ANIMACJA (SPÓJNA Z SOLVEPROBLEM) ---
const problemTransitionVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.2, ease: "easeIn" } }
};

const ViewSolvedProblem = () => {
    // --- STAN I NAWIGACJA ---
    const location = useLocation();
    const navigate = useNavigate();
    const { width } = useWindowSize();

    const problems = location.state?.problems || [];
    const initialProblemIndex = location.state?.problem_index || 0;
    const [currentProblemIndex, setCurrentProblemIndex] = useState(initialProblemIndex);

    const problem = problems[currentProblemIndex];

    // --- LOGIKA ---
    const [accountStatus] = useState(readStoredStatus);
    const dailyGoalProgress = 0;

    const getVisualLengthEstimate = (text) => {
        if (!text) return 0;
        const sanitizedText = text.replace(/\\[a-zA-Z]+|\$|{|}/g, '');
        return sanitizedText.length;
    };

    const gridClass = useMemo(() => {
        if (!problem?.answers) return "grid-cols-2";
        let dynamicThreshold = (width < 768) ? 15 : 40;
        const isAnyAnswerLong = problem.answers.some(
            answer => getVisualLengthEstimate(answer.text) > dynamicThreshold
        );
        return isAnyAnswerLong ? "grid-cols-1" : "grid-cols-2";
    }, [problem, width]);

    const progressContainerRef = useRef(null);
    const [maxVisibleCircles, setMaxVisibleCircles] = useState(0);

    useEffect(() => {
        const calculateMaxCircles = () => {
            if (progressContainerRef.current) {
                const containerWidth = progressContainerRef.current.getBoundingClientRect().width;
                if (containerWidth > 0) {
                    const fitCount = Math.floor(containerWidth / 30);
                    setMaxVisibleCircles(fitCount > 0 ? fitCount : 1);
                }
            }
        };
        setTimeout(calculateMaxCircles, 0);
        window.addEventListener('resize', calculateMaxCircles);
        return () => window.removeEventListener('resize', calculateMaxCircles);
    }, [currentProblemIndex]);

    // --- FUNKCJE RENDERUJĄCE ---
    
    const renderEloChange = (change, startElo) => {
        if (change === null || change === undefined || change === 0) return null;

        let displayChange = change;

        if (change < 0) {
            if (startElo === 0) {
                return null;
            }
            if (startElo + change < 0) {
                displayChange = -startElo;
            }
        }

        const signText = displayChange > 0 ? `+${displayChange}` : `${displayChange}`;
        
        return (
            <span className={`ml-1 font-thin transition-all duration-500 transform ${displayChange > 0 ? "text-[#007D00]" : "text-[#8A0000]"}`}>
                {signText}
            </span>
        );
    };

    const renderProgressCircles = () => {
        if (maxVisibleCircles === 0 || !problems.length) return null;
        
        let circlesToRender = [];
        for (let i = 0; i < problems.length; i++) {
            const problemData = problems[i];
            const isCurrent = i === currentProblemIndex;
            let circleClass = "w-4 h-4 rounded-full flex items-center justify-center text-[0.6rem] font-bold shrink-0 text-white cursor-pointer ";
            
            if (isCurrent) {
                circleClass += "bg-[#233855] ring-2 ring-offset-2 ring-[#233855] shadow";
            } else if (problemData) {
                circleClass += problemData.is_solved ? "bg-[#007D00]" : "bg-[#8A0000]";
            }
            
            circlesToRender.push(<div key={i} className={circleClass} onClick={() => setCurrentProblemIndex(i)}>{i + 1}</div>);
        }
        
        if (circlesToRender.length > maxVisibleCircles) {
            const half = Math.floor(maxVisibleCircles / 2);
            let start = Math.max(0, currentProblemIndex - half);
            let end = start + maxVisibleCircles;
            if (end > circlesToRender.length) {
                end = circlesToRender.length;
                start = Math.max(0, end - maxVisibleCircles);
            }
            return circlesToRender.slice(start, end);
        }
        return circlesToRender;
    };
    
    // --- HANDLERY NAWIGACJI ---
    const goToPreviousProblem = () => {
        if (currentProblemIndex > 0) {
            setCurrentProblemIndex(currentProblemIndex - 1);
        }
    };

    const goToNextProblem = () => {
        if (currentProblemIndex < problems.length - 1) {
            setCurrentProblemIndex(currentProblemIndex + 1);
        }
    };

    // --- GŁÓWNY RENDER ---
    if (!problem) {
        return (
            <PageTestPage showNav={false} accountStatus={accountStatus} dailyGoalProgress={dailyGoalProgress}>
                <div className="text-center py-10 flex flex-col items-center gap-4">
                    <p className="text-xl text-gray-600">Brak danych zadania do wyświetlenia.</p>
                     <button
                        onClick={() => navigate("/dashboard")}
                        className="w-full max-w-xs rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081]"
                    >
                        POWRÓT DO PANELU
                    </button>
                </div>
            </PageTestPage>
        );
    }

    const userEloBeforeChange = problem.user_elo - (problem.elo_change || 0);
    const hasActualHint = problem.hint && problem.hint.trim() !== "";
    const hasLinkedFormulas = problem.linked_formulas && problem.linked_formulas.length > 0;

    return (
        <PageTestPage
            showNav={false}
            reportIssueData={{ problem_id: problem.problem_id, field_id: problem.field_id }}
            accountStatus={accountStatus}
            dailyGoalProgress={dailyGoalProgress}
        >
            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0">

                {/* NAWIGACJA GÓRNA */}
                <div className="w-full flex justify-between items-center px-2 mt-[4vh] mb-3">
                    <button 
                        onClick={goToPreviousProblem} 
                        disabled={currentProblemIndex === 0} 
                        className="px-3 py-1 bg-gray-200 rounded text-xs font-normal shadow-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        &lt; Poprzednie
                    </button>
                    <button 
                        onClick={goToNextProblem} 
                        disabled={currentProblemIndex === problems.length - 1} 
                        className="px-3 py-1 bg-gray-200 rounded text-xs font-normal shadow-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Następne &gt;
                    </button>
                </div>

                {/* GŁÓWNY BIAŁY BOX */}
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.35)] border border-gray-200 py-6 px-5 lg:px-14 w-full overflow-hidden">
                    
                    {/* ANIMACJA TREŚCI */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentProblemIndex}
                            variants={problemTransitionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            className="w-full space-y-6"
                        >
                            {/* Sekcja ELO */}
                            <div className="w-full flex justify-center items-baseline flex-wrap gap-x-3 sm:gap-x-4 gap-y-2 text-xs sm:text-base font-apotek text-gray-700 lg:mt-[0.5vh]">
                                <div className="flex items-baseline gap-2">
                                    <span className="uppercase font-thin text-xs lg:text-xs text-[#233855]">Twoje ELO</span>
                                    <span className="text-[#8A0000] text-xs lg:text-xs flex items-baseline">
                                        {userEloBeforeChange}
                                        {renderEloChange(problem.elo_change, userEloBeforeChange)}
                                    </span>
                                </div>
                                <span className="text-gray-400 font-thin text-xs lg:text-xs">VS</span>
                                <div className="flex items-baseline gap-2">
                                    <span className="font-thin text-xs lg:text-xs text-[#8A0000]">{problem.problem_elo}</span>
                                    <span className="uppercase text-xs lg:text-xs text-[#233855]">ELO Zadania</span>
                                </div>
                            </div>

                            {/* Pytanie */}
                            <p className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex items-center justify-center lg:mt-[0.5vh]">
                                <RenderMarkdownWithLatex text={problem.points_assigned ? `(${problem.points_assigned} pkt) ${problem.question}` : problem.question} />
                            </p>

                            {/* Obrazek */}
                            {problem.image_link && (
                                <div className="flex justify-center py-2">
                                    <img 
                                        src={
                                            problem.image_link.startsWith('http') 
                                            ? problem.image_link 
                                            : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${problem.image_link}`
                                        } 
                                        alt="Ilustracja zadania" 
                                        className="max-w-full h-auto rounded border" 
                                    />
                                </div>
                            )}
                            
                            {/* PODPOWIEDŹ (ZAMIAST RĘCZNEGO IF-A) */}
                            {(hasActualHint || hasLinkedFormulas) && (
                                <HintAndFormulas 
                                    hintText={problem.hint} 
                                    formulas={problem.linked_formulas} 
                                />
                            )}

                            {/* Siatka Odpowiedzi */}
                            <div className={`grid ${gridClass} gap-2 w-full`}>
                                {problem.answers.map((answer, index) => {
                                    const isCorrect = answer.isCorrect;
                                    const isUserSelected = problem.selected_answer === answer.text;
                                    let buttonClass = "flex items-center justify-center rounded-lg text-[0.8rem] text-slate-100 px-3 py-2 font-normal tracking-wider shadow-md transition-all duration-200 disabled:cursor-not-allowed min-h-[20px] w-full";
                                    if (isCorrect) buttonClass += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
                                    else if (isUserSelected) buttonClass += " bg-[#8A0000] opacity-90";
                                    else buttonClass += " bg-[#233855] opacity-40";
                                    return (<button key={index} disabled className={buttonClass}><RenderMarkdownWithLatex text={answer.text} /></button>);
                                })}
                            </div>

                            {/* Rozwiązanie (ZAMIAST SZAREGO DIVA) */}
                            <div className="w-full">
                                <SolutionStepper solutionText={problem.solution || "Brak rozwiązania."} />
                            </div>

                        </motion.div>
                    </AnimatePresence>
                </div>
                
                {/* PRZYCISK POWROTU DO STATYSTYK */}
                <div className="w-full pt-2 mt-[2vh] lg:mt-[3vh]">
                    <button 
                        onClick={() => navigate("/end-session-stats")}
                        className="flex w-full justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081]"
                    >
                        POWRÓT DO STATYSTYK
                    </button>
                </div>

                {/* Spacer */}
                <div className="h-[12vh] flex-shrink-0" />
            </div>

            <div className="flex-grow" />

            {/* PRZYKLEJONY DOLNY PASEK */}
            <div className="sticky bottom-0 lg:bottom-[1vh] w-full py-3 bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent">
                <div className="max-w-xl mx-auto space-y-3 p-2">
                    <div ref={progressContainerRef} className="flex flex-row flex-nowrap justify-center items-center space-x-2.5">
                        {renderProgressCircles()}
                    </div>
                    {problem?.linked_matura_problems?.length > 0 && (
                        <div className="w-full text-center text-xs text-gray-500 italic">
                            Na podstawie: {problem.linked_matura_problems.map(linkedPb => linkedPb.source).join(', ')}
                        </div>
                    )}
                </div>
            </div>
        </PageTestPage>
    );
};

export default ViewSolvedProblem;